import React, { useState, useMemo, useEffect, useRef } from "react";
import { IonToolbar, IonInput, IonButton, IonText, IonButtons, useIonViewWillEnter } from "@ionic/react";
import classNames from "classnames/bind";
import styles from "./ProductsSearchBarHeader.module.scss";
import { useConfig } from "../../../../providers/config";
import { ProductsSearchBarHeaderProps } from "@core/types";
import { useSearchParams } from "../../../../hooks/useSearchParams";
import { debounce } from "lodash";
import BackButton from "../ui/back-button";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const ProductsSearchBarHeader = ({
  onClick,
  onCancel,
  onClean,
  showBackButton = false,
  showCancelButton = false,
  ...inputProps
}: ProductsSearchBarHeaderProps) => {
  const { t } = useTranslation();
  const config = useConfig();
  const inputRef = useRef<HTMLIonInputElement>(null);
  const { set: setSearchParam, delete: deleteSearchParam, get: getSearchParam } = useSearchParams();
  const [searchText, setSearchText] = useState(getSearchParam("q") || "");

  const debouncedSearch = useMemo(() => {
    return debounce((value: string) => {
      if (value.length > 0) {
        setSearchParam("q", value);
      } else {
        deleteSearchParam("q");
      }
    }, config.search.inputDebounceTimeMs);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useIonViewWillEnter(() => {
    if (inputProps.autofocus && inputRef.current) {
      const input = inputRef.current.querySelector("input");
      input?.focus();
    }
  }, []);

  const handleSearch = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <IonToolbar className={cx("ion-no-padding", "parhato2Toolbar")}>
      <form className={cx("searchBox__form")} onSubmit={handleSubmit}>
        {showBackButton && (
          <IonButtons slot="start" className={cx("searchBox__buttons")}>
            <BackButton buttonStyles="!py-2.5 !px-" />
          </IonButtons>
        )}
        <IonInput
          ref={inputRef}
          clearInput
          type="search"
          value={searchText}
          onClick={onClick}
          className={cx("searchInput")}
          placeholder={t("Search in the store")}
          onIonChange={(e) => handleSearch(e.detail.value || "")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // блокируем Enter
            }
          }}
          {...inputProps}
        />
        {showCancelButton && (
          <IonButton slot="end" className={cx("cancelButton")} onClick={onCancel} type="button">
            <IonText className={cx("cancelButton__text")}>{t("Cancel")}</IonText>
          </IonButton>
        )}
      </form>
    </IonToolbar>
  );
};

ProductsSearchBarHeader.displayName = "PARHATO2_ProductsSearchBarHeader";

export default ProductsSearchBarHeader;
